import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FolderOpen, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

const COPY = {
  view: { title: 'Programmes using this lab', desc: 'These programme activities link to this lab.' },
  edit: { title: 'This lab is used in programmes', desc: 'Changes you make will apply to every programme below, including learners already working through them.' },
  delete: { title: "Can't delete this lab yet", desc: 'Unlink the lab from these programme activities first, then delete it.' },
};

export const LabUsageBadge = ({ count, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${count
      ? 'bg-teal-500/15 text-teal-300 hover:bg-teal-500/25'
      : 'bg-zinc-800 text-zinc-500 cursor-default'}`}
    disabled={!count}
    data-testid={testId}
  >
    {count ? `Used in ${count} programme${count === 1 ? '' : 's'}` : 'Not used in programmes'}
  </button>
);

export const countProgrammes = (usage = []) => new Set(usage.map((u) => u.programme_id)).size;

export default function LabUsageDialog({ open, mode = 'view', labTitle, usage = [], onClose, onContinue }) {
  const copy = COPY[mode] || COPY.view;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg" data-testid="lab-usage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode !== 'view' && <AlertTriangle className={`h-5 w-5 ${mode === 'delete' ? 'text-red-400' : 'text-amber-400'}`} />}
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            <span className="text-zinc-200 font-medium">{labTitle}</span> — {copy.desc}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {usage.map((u) => (
            <Link
              key={u.activity_id}
              to={`/admin/programmes/${u.programme_id}/edit`}
              onClick={onClose}
              className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-950/50 hover:border-teal-600/50 transition-colors"
              data-testid={`lab-usage-item-${u.activity_id}`}
            >
              <FolderOpen className="h-4 w-4 text-teal-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{u.programme_name}</p>
                <p className="text-xs text-zinc-500 truncate">
                  {[u.period_title, u.activity_title].filter(Boolean).join(' · ')}
                  {u.programme_published ? '' : ' · draft programme'}
                </p>
              </div>
              <ExternalLink className="h-3.5 w-3.5 text-zinc-500" />
            </Link>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700" data-testid="lab-usage-close-btn">
            {mode === 'edit' ? 'Cancel' : 'Close'}
          </Button>
          {mode === 'edit' && (
            <Button onClick={onContinue} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="lab-usage-continue-edit-btn">
              Continue to edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
